import React from 'react';
import { Stage, Layer, Group, Image } from 'react-konva';
import { useImageTransform } from '../../hooks/useImageTransform';
import { ImageGrid } from '../ImageGrid';
import { ImageState } from '../../model/types';

type TransformedImageProps = {
  image: HTMLImageElement | null;
  containerSize: { width: number; height: number };
  modifications: ImageState;
  fitContainer?: boolean;
};

export const TransformedImage = ({
  image,
  containerSize,
  modifications,
  fitContainer = false,
}: TransformedImageProps) => {
  const transform = useImageTransform({
    image,
    containerSize,
    modifications,
    fitContainer,
  });

  if (!transform) return null;

  const {
    groupWidth,
    groupHeight,
    imageX,
    imageY,
    imageScale,
    rotationAngleDegree,
    scaleX,
    scaleY,
  } = transform;
  const zoom = modifications.zoom || 1;
  const zoomedGroupWidth = groupWidth * zoom;
  const zoomedGroupHeight = groupHeight * zoom;

  if (fitContainer)  {
    return (
      <Stage
        width={containerSize.width}
        height={containerSize.height}
        style={{
          transform: `scaleX(${modifications?.isFlipped ? -1 : 1})`,
        }}
      >
        <Layer>
          <Group
            x={containerSize.width / 2}
            y={containerSize.height / 2}
            width={groupWidth}
            height={groupHeight}
            clipX={-groupWidth / 2}
            clipY={-groupHeight / 2}
            clipWidth={groupWidth}
            clipHeight={groupHeight}
          >
            <Image
              image={image as CanvasImageSource}
              rotation={rotationAngleDegree || 0}
              x={imageX}
              y={imageY}
              scaleX={scaleX}
              scaleY={scaleY}
              offsetX={image!.width / 2}
              offsetY={image!.height / 2}
            />
          </Group>
        </Layer>
      </Stage>
    );
  }

  return (
    <Stage
      width={zoomedGroupWidth}
      height={zoomedGroupHeight}
      style={{
        transform: `rotate(${modifications?.rotate || 0}deg) scaleX(${
          modifications.isFlipped ? -1 : 1
        })`,
        transition: 'transform 0.2s ease-in-out',
      }}
    >
      <Layer>
        <Group
          x={zoomedGroupWidth / 2}
          y={zoomedGroupHeight / 2}
          scaleX={modifications?.zoom}
          scaleY={modifications?.zoom}
        >
          <Image
            image={image as CanvasImageSource}
            rotation={rotationAngleDegree}
            x={imageX - groupWidth / 2}
            y={imageY - groupHeight / 2}
            scaleX={imageScale}
            scaleY={imageScale}
            offsetX={image!.width / 2}
            offsetY={image!.height / 2}
          />
          {modifications.grid && (
            <ImageGrid width={groupWidth} height={groupHeight} zoom={zoom} />
          )}
        </Group>
      </Layer>
    </Stage>
  );
};
